import { useState, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { Spinner } from 'react-bootstrap'
import { useParams } from 'react-router-dom'
import {
  PageDescription,
  PageTitle,
  SearchCircle,
  Typography,
} from '../components/styled'
import LanguagesDropdown from '../components/languageDropdown'
import FullLogo from '../assets/logos/full-logo.png'
import theme from '../constants/theme'
import apiReferenceContacts from '../services/api/referenceContacts'
import apiReferenceFormConfig from '../services/api/referenceFormConfig'
import CheckOne from '../assets/svgs/checkOne'
import ExclamationCircle from '../assets/svgs/exclamationCircle'
import { Page, TopBar } from '../components/referenceRequest/styled'
import { FormWrapper } from '../components/referenceFeedback/styled'
import FeedbackFormBody from '../components/referenceFeedback/FeedbackFormBody'

const RESULT_TYPE = { SUCCESS: 'success', ERROR: 'error' }

function ReferenceFeedbackForm() {
  const { t } = useTranslation()
  const { contactToken: token } = useParams()
  const [referenceDetails, setReferenceDetails] = useState({})
  const [formConfig, setFormConfig] = useState([])
  const [message, setMessage] = useState()
  const [resultType, setResultType] = useState(null)
  const [isLoading, setIsLoading] = useState(false)

  const isSubmittedOrExpired = resultType !== null

  const RESULT_CONFIG = {
    [RESULT_TYPE.SUCCESS]: {
      icon: <CheckOne width="50px" height="50px" />,
      title: t('reference.formSubmittedTitle'),
      borderColor: theme.success,
    },
    [RESULT_TYPE.ERROR]: {
      icon: (
        <ExclamationCircle
          width="50px"
          height="50px"
          color={theme.talentinoWarning}
        />
      ),
      title: t('dataManagement.somethingWrongMsg'),
      borderColor: theme.talentinoWarning,
    },
  }

  const config = RESULT_CONFIG[resultType] || RESULT_CONFIG[RESULT_TYPE.ERROR]

  const getContactDetails = useCallback(async () => {
    setIsLoading(true)
    try {
      const res = await apiReferenceContacts.getSingle(token)
      setReferenceDetails(res)
      const configRes = await apiReferenceFormConfig.getByCompany(
        res?.candidateInfo?.companyId,
      )
      setFormConfig(configRes?.items || [])
    } catch (err) {
      const status = err?.response?.status
      if (status === 409) setMessage(t('reference.formAlreadySubmitted'))
      else if (status === 403) setMessage(t('reference.formPastDeadline'))
      else
        setMessage(
          `${t('dataManagement.somethingWrongMsg')}: ${err?.response?.data?.description ?? ''}`,
        )
      setResultType(RESULT_TYPE.ERROR)
    } finally {
      setIsLoading(false)
    }
  }, [token, t])

  useEffect(() => {
    getContactDetails()
  }, [getContactDetails])

  const handleSubmit = (payload, setLoading) => {
    apiReferenceContacts
      .put(token, payload)
      .then((res) => {
        setResultType(RESULT_TYPE.SUCCESS)
        setMessage(res?.msg || t('reference.thankYou'))
      })
      .catch((err) => {
        setResultType(RESULT_TYPE.ERROR)
        setMessage(
          err?.response?.data?.description ||
            t('dataManagement.somethingWrongMsg'),
        )
      })
      .finally(() => setLoading(false))
  }

  if (isLoading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: '100vh', background: '#F4F7FB' }}
      >
        <Spinner />
      </div>
    )
  }

  return (
    <Page>
      <TopBar className="d-flex align-items-center justify-content-between px-3">
        <img width="130px" src={FullLogo} alt="logo" />
        {!isSubmittedOrExpired && <LanguagesDropdown />}
      </TopBar>

      {isSubmittedOrExpired ? (
        <div className="d-flex flex-grow-1 flex-column justify-content-center align-items-center">
          <SearchCircle style={{ borderColor: `${config.borderColor}30` }}>
            {config.icon}
          </SearchCircle>
          <PageTitle>{config.title}</PageTitle>
          <PageDescription>{message}</PageDescription>
        </div>
      ) : (
        <FormWrapper>
          <div className="text-center mb-3">
            <Typography
              className="d-block mb-2"
              bold
              size="26px"
              color={theme.brandColorOrange}
            >
              {t('reference.referenceFeedbackForm')}
            </Typography>
            <Typography size="15px" className="text-muted">
              {t('reference.pleaseProvideFeedback')}
            </Typography>
          </div>

          <FeedbackFormBody
            referenceDetails={referenceDetails}
            formConfig={formConfig}
            onSubmit={handleSubmit}
          />
        </FormWrapper>
      )}
    </Page>
  )
}

export default ReferenceFeedbackForm
